"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const ROLES = [
    { icon: "cloud-bold", t: "Nas_everywhere_r1_t", d: "Nas_everywhere_r1_d" },
    { icon: "shield-check-bold-duotone", t: "Nas_everywhere_r2_t", d: "Nas_everywhere_r2_d" },
    { icon: "gallery-bold-duotone", t: "Nas_everywhere_r3_t", d: "Nas_everywhere_r3_d" },
    { icon: "clapperboard-play-bold", t: "Nas_everywhere_r4_t", d: "Nas_everywhere_r4_d" },
    { icon: "videocamera-record-bold", t: "Nas_everywhere_r5_t", d: "Nas_everywhere_r5_d" },
    { icon: "users-group-rounded-bold-duotone", t: "Nas_everywhere_r6_t", d: "Nas_everywhere_r6_d" },
];

const REPLACES = [
    { name: "Dropbox", logo: "logos:dropbox" },
    { name: "Google Drive", logo: "logos:google-drive" },
    { name: "Google Photos", logo: "logos:google-photos" },
    { name: "OneDrive", logo: "logos:microsoft-onedrive" },
    { name: "iCloud", logo: "logos:apple" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};
const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function NasEverywhere() {
    const t = useTranslations();

    return (
        <section id="nas-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Nas_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Nas_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-teal-500/15 ring-1 ring-teal-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="server-bold-duotone"
                            className="w-3.5 h-3.5 text-teal-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-300">
                            {t("Nas_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Nas_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-teal-300 to-emerald-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des rôles */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                    >
                        {ROLES.map((r) => (
                            <motion.div
                                key={r.t}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div className="w-11 h-11 rounded-xl bg-teal-500/15 ring-1 ring-teal-400/30 flex items-center justify-center mb-4">
                                    <SolarIcon
                                        name={r.icon}
                                        className="w-5 h-5 text-teal-300"
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(r.t)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(r.d)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Remplace ces abonnements */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-teal-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Nas_everywhere_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                            {REPLACES.map((s) => (
                                <span
                                    key={s.name}
                                    className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-black/5 px-2.5 py-1.5"
                                >
                                    <Icon
                                        icon={s.logo}
                                        className="w-4 h-4"
                                        aria-hidden="true"
                                    />
                                    <span className="text-[12px] font-semibold text-slate-700 line-through decoration-rose-400/70">
                                        {s.name}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
